import { tetriToDecimalString } from '@/utils/money';
import type {
    AccountType,
    LocaleCode,
    PlausibilityReport,
    PlausibilityWarning,
    Statement,
    StatementComparative,
    StatementKind,
    StatementLine,
    StatementLineAccount,
    StatementLineKind,
    StatementLineSign,
    StatementWindow,
    UnmappedAccount,
} from '@/api/models';

/**
 * Wire ⇄ domain mapping for financial statements (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hooks, the renderer — sees
 * the domain shapes in `@/api/models/Statements`. If the wire disagrees with
 * what is assumed here, this file is the whole of the fix. Same seam, and the
 * same reason, as `accountsMapping` and `journalMapping`.
 *
 * The client's original frontend-authored contract diverged from the shipped API
 * in nearly every field. Measured against `/openapi/v1.json` and live responses:
 *
 * | domain | wire |
 * | --- | --- |
 * | `PROFIT_AND_LOSS` | `PROFIT_LOSS` |
 * | `id` | `lineId` |
 * | `label` (was a locale map) | `label` — a **string, already resolved** to the requested `locale` |
 * | `amountTetri` / `amount` | `valueTetri` / `value` |
 * | `priorAmountTetri` / `priorAmount` | `comparativeValueTetri` / `comparativeValue` (nullable) |
 * | `deltaTetri` / `deltaAmount` | `deltaTetri` / `delta` (nullable) — **server-computed** |
 * | `sign: 'POSITIVE' \| 'NEGATIVE'` | `sign: 1 \| -1` |
 * | `kind: SECTION\|LINE\|SUBTOTAL\|TOTAL\|RESULT` | `kind: ACCOUNTS\|SUBTOTAL\|COMPUTED` |
 * | `accounts[].accountCode` / `accountName` / `amountTetri` | `accounts[].code` / `name` (string) / `valueTetri` |
 * | `window` / `comparativeWindow` | flat `asOf`, `dateFrom`, `dateTo`, `comparativeAsOf`, `comparativeDateFrom`, `comparativeDateTo` |
 * | warnings on the statement | **absent** — a separate `/plausibility` call |
 *
 * Three properties of the wire this mapping deliberately preserves rather than
 * "corrects", because correcting any of them reintroduces a shipped bug:
 *
 * 1. **`valueTetri` is natural-terms; `sign` is presentation only.** Accumulated
 *    depreciation arrives as `+15_000_00` with `sign: -1`. The renderer applies
 *    the sign when it prints the figure and nowhere else.
 * 2. **`deltaTetri` is server-computed, in natural terms, sign excluded.** It is
 *    carried through untouched. Recomputing it from current − prior, or applying
 *    `sign` to it, is what made a growing contra line read as a decrease.
 * 3. **Order is the wire's order.** Lines are mapped in place. The seed's order
 *    is the rendered order — including the fact that this seed puts non-current
 *    assets before current assets, and equity before liabilities.
 *
 * A line that cannot be mapped is **dropped, not guessed at** — a statement row
 * placed at the wrong depth or with the wrong sign is a reporting error, and a
 * silently misplaced figure is worse than a missing one. {@link mapStatement}
 * reports what it dropped.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

export type StatementLineAccountWire = {
    accountId?: string;
    code?: string;
    name?: string;
    value?: string | number | null;
    valueTetri?: number;
};

export type RenderedStatementLineWire = {
    lineId?: string;
    label?: string;
    valueTetri?: number;
    value?: string | number | null;
    sign?: number;
    kind?: string;
    depth?: number;
    comparativeValueTetri?: number | null;
    comparativeValue?: string | number | null;
    deltaTetri?: number | null;
    delta?: string | number | null;
    accounts?: StatementLineAccountWire[] | null;
};

export type UnmappedAccountWire = {
    accountId?: string;
    code?: string;
    name?: string;
    type?: string;
};

export type StatementWire = {
    statement?: string;
    asOf?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    locale?: string | null;
    comparative?: string | null;
    comparativeAsOf?: string | null;
    comparativeDateFrom?: string | null;
    comparativeDateTo?: string | null;
    lines?: RenderedStatementLineWire[] | null;
    unmappedAccounts?: UnmappedAccountWire[] | null;
};

export type PlausibilityWarningWire = {
    rule?: string;
    message?: string;
    lineId?: string | null;
    accountId?: string | null;
    accountCode?: string | null;
    accountName?: string | null;
    amountTetri?: number | null;
};

export type PlausibilityWire = {
    asOf?: string | null;
    warnings?: PlausibilityWarningWire[] | null;
};

// ── primitives ──────────────────────────────────────────────────────────────

const ACCOUNT_TYPES: readonly string[] = [
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'REVENUE',
    'EXPENSE',
];

const LINE_KINDS: readonly string[] = ['ACCOUNTS', 'SUBTOTAL', 'COMPUTED'];

const isLineKind = (value: unknown): value is StatementLineKind =>
    typeof value === 'string' && LINE_KINDS.includes(value);

/**
 * The decimal companion, when the wire sends one, else derived from the integer.
 *
 * The integer is authoritative throughout (the project money contract), so the
 * companion is only ever a display echo — deriving it when absent removes a
 * class of "field missing, cell blank" failure at no cost.
 */
const decimalEcho = (
    wire: string | number | null | undefined,
    tetri: number,
): string => (typeof wire === 'string' ? wire : tetriToDecimalString(tetri));

/**
 * `1` unless the wire explicitly says `-1`.
 *
 * A missing or unrecognised sign defaults to positive rather than dropping the
 * line: the figure is still correct in natural terms, only its presentation
 * polarity is unknown, and losing the row entirely would unbalance the
 * statement.
 */
const signOf = (wire: number | undefined): StatementLineSign =>
    wire === -1 ? -1 : 1;

const isFiniteInt = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

// ── lines ───────────────────────────────────────────────────────────────────

const mapLineAccount = (
    wire: StatementLineAccountWire,
): StatementLineAccount | null => {
    if (!wire.accountId || !isFiniteInt(wire.valueTetri)) {
        return null;
    }
    return {
        accountId: wire.accountId,
        accountCode: wire.code ?? '',
        accountName: wire.name ?? wire.code ?? '',
        amount: decimalEcho(wire.value, wire.valueTetri),
        amountTetri: wire.valueTetri,
    };
};

/** `null` when the row cannot be placed or signed with confidence. */
export const mapStatementLine = (
    wire: RenderedStatementLineWire,
): StatementLine | null => {
    if (
        !wire.lineId ||
        !isLineKind(wire.kind) ||
        !isFiniteInt(wire.valueTetri) ||
        !isFiniteInt(wire.depth)
    ) {
        return null;
    }

    // Both are nullable on the wire: no comparative requested, or no prior
    // window. `null` downstream means "no figure", which the renderer shows as an
    // en dash rather than as a zero.
    const priorTetri = isFiniteInt(wire.comparativeValueTetri)
        ? wire.comparativeValueTetri
        : null;
    const deltaTetri = isFiniteInt(wire.deltaTetri) ? wire.deltaTetri : null;

    return {
        id: wire.lineId,
        label: wire.label ?? '',
        depth: wire.depth,
        kind: wire.kind,
        sign: signOf(wire.sign),
        amount: decimalEcho(wire.value, wire.valueTetri),
        amountTetri: wire.valueTetri,
        priorAmount:
            priorTetri === null
                ? null
                : decimalEcho(wire.comparativeValue, priorTetri),
        priorAmountTetri: priorTetri,
        // Carried through verbatim. See this file's header, point 2.
        deltaAmount:
            deltaTetri === null ? null : decimalEcho(wire.delta, deltaTetri),
        deltaTetri,
        // Absent on SUBTOTAL / COMPUTED — an empty list, never a fabricated one.
        accounts: (wire.accounts ?? [])
            .map(mapLineAccount)
            .filter(
                (account): account is StatementLineAccount => account !== null,
            ),
    };
};

// ── windows ─────────────────────────────────────────────────────────────────

const windowOf = (
    asOf: string | null | undefined,
    from: string | null | undefined,
    to: string | null | undefined,
): StatementWindow => ({
    asOf: asOf ?? null,
    from: from ?? null,
    to: to ?? null,
});

const isEmptyWindow = (window: StatementWindow): boolean =>
    window.asOf === null && window.from === null && window.to === null;

// ── statement ───────────────────────────────────────────────────────────────

const kindOf = (wire: string | undefined): StatementKind | null => {
    if (wire === 'BALANCE_SHEET') {
        return 'BALANCE_SHEET';
    }
    // The wire spells it `PROFIT_LOSS`; the domain keeps `PROFIT_AND_LOSS`.
    return wire === 'PROFIT_LOSS' ? 'PROFIT_AND_LOSS' : null;
};

const comparativeOf = (
    wire: string | null | undefined,
): StatementComparative => (wire === 'PRIOR_YEAR' ? 'PRIOR_YEAR' : 'NONE');

const localeOf = (wire: string | null | undefined): LocaleCode =>
    wire === 'ka' ? 'ka' : 'en';

const mapUnmappedAccount = (
    wire: UnmappedAccountWire,
): UnmappedAccount | null => {
    if (!wire.accountId || !ACCOUNT_TYPES.includes(wire.type ?? '')) {
        return null;
    }
    return {
        accountId: wire.accountId,
        accountCode: wire.code ?? '',
        accountName: wire.name ?? wire.code ?? '',
        type: wire.type as AccountType,
    };
};

export type MappedStatement = {
    statement: Statement;
    /** `lineId`s (or `'<unidentified>'`) the mapping refused to place. */
    droppedLineIds: string[];
};

/**
 * Map a statement response. `null` only when the payload is not a statement at
 * all (an unrecognised `statement` kind) — a partially unreadable line list
 * still yields a statement, with the unreadable rows reported in
 * `droppedLineIds` rather than guessed at.
 */
export const mapStatement = (wire: StatementWire): MappedStatement | null => {
    const kind = kindOf(wire.statement);
    if (!kind) {
        return null;
    }

    const droppedLineIds: string[] = [];
    const lines: StatementLine[] = [];
    for (const lineWire of wire.lines ?? []) {
        const line = mapStatementLine(lineWire);
        if (line) {
            lines.push(line);
        } else {
            droppedLineIds.push(lineWire.lineId ?? '<unidentified>');
        }
    }

    const comparative = comparativeOf(wire.comparative);
    const comparativeWindow = windowOf(
        wire.comparativeAsOf,
        wire.comparativeDateFrom,
        wire.comparativeDateTo,
    );

    return {
        statement: {
            kind,
            locale: localeOf(wire.locale),
            window: windowOf(wire.asOf, wire.dateFrom, wire.dateTo),
            comparative,
            // A `PRIOR_YEAR` request with no window back is treated as no
            // comparative: a column whose header cannot be labelled honestly is
            // a column that should not be drawn.
            comparativeWindow:
                comparative === 'NONE' || isEmptyWindow(comparativeWindow)
                    ? null
                    : comparativeWindow,
            lines,
            unmappedAccounts: (wire.unmappedAccounts ?? [])
                .map(mapUnmappedAccount)
                .filter(
                    (account): account is UnmappedAccount => account !== null,
                ),
        },
        droppedLineIds,
    };
};

// ── plausibility ────────────────────────────────────────────────────────────

/**
 * Warnings are advisories, so mapping is lenient by design: a notice this app
 * has no locale text for still carries the server's message, and refusing to
 * show it would turn "we could not read this advisory" into silence.
 *
 * BOS-163: a notice is kept when it has **either** a recognised rule (the
 * client can compose the sentence itself) or a message (it cannot, but the
 * server said something). Only one with neither is dropped — there would be
 * nothing to display.
 */
export const mapPlausibility = (
    wire: PlausibilityWire,
): PlausibilityReport => ({
    asOf: wire.asOf ?? null,
    warnings: (wire.warnings ?? [])
        .filter((warning) => Boolean(warning.rule) || Boolean(warning.message))
        .map((warning): PlausibilityWarning => ({
            rule: warning.rule ?? '',
            message: warning.message ?? '',
            lineId: warning.lineId ?? null,
            accountId: warning.accountId ?? null,
            accountCode: warning.accountCode ?? null,
            accountName: warning.accountName ?? null,
            amountTetri:
                typeof warning.amountTetri === 'number'
                    ? warning.amountTetri
                    : null,
        })),
});
